// react
import { useContext } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

// context
import { ContextProject } from '../Project.context';
import { ContextSpendPlan } from '../../ProjectV2/ProjectV2.context';
import { ContextUser } from '../../../../App.context';

// mui
import {
	alpha,
	Avatar,
	Box,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	ChipProps,
	Container,
	Grid,
	Stack,
	Typography,
	useTheme,
} from '@mui/material';

// cards
import { CardAction, CardActionProps } from '../../../cards/CardAction/CardAction';

// dialogs
import { DialogDownloadReport } from '../../../dialogs/DialogDownloadReport/DialogDownloadReport';

// utils
import { getReports } from '../../../../utils/reports';

// icons
import {
	ArrowForward as IconArrow,
	Assessment as IconReport,
	DownloadOutlined as IconDownload,
	Payments as IconPayments,
	Savings as IconEarmark,
	Speed as IconDashboard,
	Timeline as IconTimeline,
	TrendingUp as IconTrending,
} from '@mui/icons-material';

// v2 listing header
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// Report Card (used when (project.version === '2'))

interface ReportCardProps {
	title: string;
	description: string;
	icon: React.ElementType;
	color: string;
	stats?: { label: string; value: string | number }[];
	onClick: () => void;
}

const ReportCard = ({ title, description, icon: Icon, color, stats, onClick }: ReportCardProps) => {
	const theme = useTheme();
	const { t } = useTranslation();

	return (
		<Card
			sx={{
				height: '100%',
				borderRadius: 3,
				transition: 'all 0.3s ease',
				'&:hover': {
					transform: 'translateY(-4px)',
					boxShadow: `0 12px 24px ${alpha(color, 0.25)}`,
				},
			}}>
			<CardActionArea onClick={onClick} sx={{ height: '100%' }}>
				<CardContent sx={{ p: 3, height: '100%' }}>
					<Stack spacing={2.5} sx={{ height: '100%' }}>
						{/* Header */}
						<Stack direction="row" alignItems="flex-start" justifyContent="space-between">
							<Avatar
								sx={{
									width: 56,
									height: 56,
									bgcolor: alpha(color, 0.1),
								}}>
								<Icon sx={{ fontSize: 28, color: color }} />
							</Avatar>
							<Chip
								icon={<IconArrow sx={{ fontSize: 16 }} />}
								label={t('viewReport')}
								size="small"
								sx={{
									bgcolor: alpha(color, 0.1),
									color: color,
									fontWeight: 500,
									'& .MuiChip-icon': { color: color },
								}}
							/>
						</Stack>

						{/* Content */}
						<Box sx={{ flex: 1 }}>
							<Typography variant="h6" fontWeight={600} gutterBottom>
								{title}
							</Typography>
							<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
								{description}
							</Typography>
						</Box>

						{/* Stats */}
						{stats && stats.length > 0 && (
							<Stack
								direction="row"
								spacing={3}
								sx={{
									pt: 2,
									borderTop: `1px solid ${theme.palette.divider}`,
								}}>
								{stats.map((stat, index) => (
									<Box key={index}>
										<Typography variant="h6" fontWeight={700} color={color}>
											{stat.value}
										</Typography>
										<Typography variant="caption" color="text.secondary">
											{stat.label}
										</Typography>
									</Box>
								))}
							</Stack>
						)}
					</Stack>
				</CardContent>
			</CardActionArea>
		</Card>
	);
};

export const Reports = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const theme = useTheme();

	// context
	const { project } = useContext(ContextProject);
	const { accounts, activities, contracts, conditions, vouchers } = useContext(ContextSpendPlan);
	const { user } = useContext(ContextUser);

	// state
	const [searchParams, setSearchParams] = useSearchParams();

	// params
	const paramDialog = searchParams.get('dialog');

	const isV2 = project?.version === '2';

	// =========================================================================
	// reports (cards rendered when isV2)
	// =========================================================================
	const activityStats = {
		total: activities.length,
		active: activities.filter((a) => a.status === 'IN_PROGRESS').length,
	};

	const paymentStats = {
		total: vouchers.length,
		verified: vouchers.filter((v) => v.status === 'VERIFIED' || v.status === 'REDEEMED').length,
	};

	const pendingConditions = conditions.filter((c) => c.status === 'PENDING').length;

	const v2Reports = [
		{
			title: t('spendPlan.reports.operationsDashboard.title'),
			description: t('spendPlan.reports.operationsDashboard.description'),
			icon: IconDashboard,
			color: theme.palette.info.main,
			stats: [
				{ label: t('spendPlan.reports.totalActivities'), value: activityStats.total },
				{ label: t('contracts'), value: contracts.length },
				{ label: t('pending'), value: pendingConditions },
			],
			path: 'operations-dashboard',
		},
		{
			title: t('spendPlan.reports.activityMonitoring.title'),
			description: t('spendPlan.reports.activityMonitoring.description'),
			icon: IconTimeline,
			color: theme.palette.primary.main,
			stats: [
				{ label: t('spendPlan.reports.totalActivities'), value: activityStats.total },
				{ label: t('spendPlan.reports.activeActivities'), value: activityStats.active },
			],
			path: 'activity-monitoring',
		},
		{
			title: t('spendPlan.reports.paymentReport.title'),
			description: t('spendPlan.reports.paymentReport.description'),
			icon: IconPayments,
			color: theme.palette.success.main,
			stats: [
				{ label: t('spendPlan.reports.totalPayments'), value: paymentStats.total },
				{ label: t('spendPlan.reports.verifiedPayments'), value: paymentStats.verified },
			],
			path: 'payment-report',
		},
		{
			title: t('spendPlan.fundingEarmarks'),
			description: t('spendPlan.fundingEarmarks.report.subtitle'),
			icon: IconEarmark,
			color: '#9333ea',
			stats: [
				{ label: t('accounts'), value: accounts.length },
				{ label: t('activities'), value: activities.length },
			],
			path: 'funding-earmarks-report',
		},
	];

	const PaperProps: CardActionProps['PaperProps'] = {
		sx: {
			height: '100%',
			minHeight: {
				xs: 200,
				md: 300,
			},
		},
	};

	const ButtonProps: CardActionProps['ButtonProps'] = {
		sx: (theme) => {
			return {
				border: 'none',
				height: '100%',
				padding: theme.spacing(2),
				color: 'black',
				'&:hover': {
					border: 'none',
				},
			};
		},
	};

	const reports = getReports({ t, user });

	const cards: CardActionProps[] = [];

	// for each report
	for (let i = 0; i < reports.length; i++) {
		const report = reports[i];

		const cardActionProps: CardActionProps = {
			content: report.content,
			hasBreakLine: false,
			IconCircleProps: report.IconCircleProps,
			GridProps: report.GridProps,
			PaperProps,
			title: {
				...report.title,
				component: 'h3',
				variant: 'h2',
			},
			titleGridProps: {
				alignItems: 'center',
			},
		};

		const action = {
			children: t('downloadReport'),
		};

		let reportChipProps: ChipProps | null = null;

		const reportButtonProps: CardActionProps['ButtonProps'] = {
			...ButtonProps,
			disabled: report.disabled,
		};

		// report button props
		if (report.type === 'FUND_FLOW') {
			action.children = t('seeReport');
			reportButtonProps.component = RouterLink;
			reportButtonProps.to = `/project/${project?.idWbg}/reports/flow`;
		} else if (report.type === 'SOE') {
			action.children = t('createReport');
			reportButtonProps.component = RouterLink;
			reportButtonProps.to = `/project/${project?.idWbg}/reports/soe`;
		} else if (report.type === 'IFR') {
			action.children = t('createReport');
			reportButtonProps.component = RouterLink;
			reportButtonProps.to = `/project/${project?.idWbg}/reports/financial`;
		} else {
			// other reports
			cardActionProps.icon = <IconDownload />;
			reportButtonProps.onClick = () => {
				searchParams.set('dialog', 'downloadReport');
				searchParams.set('dialogReport', report.type);
				setSearchParams(searchParams);
			};
		}

		if (report.disabled) {
			reportChipProps = {
				label: t('comingSoon'),
				size: 'small',
				variant: 'twoTone',
			};
		}

		cards.push({
			...cardActionProps,
			action,
			ButtonProps: reportButtonProps,
			ChipProps: reportChipProps,
		});
	}

	return (
		<>
			{isV2 && (
				<HeaderListingPage
					icon={IconReport}
					title={t('reports')}
					subtitle={t('reports.subtitle')}
					variant="document"
				/>
			)}
			<Box sx={{ pt: isV2 ? 3 : 0 }}>
				{isV2 ? (
					<Container maxWidth={false}>
						{/* V2 reports */}
						<Grid container spacing={3}>
							{v2Reports.map((report) => (
								<Grid item xs={12} sm={6} lg={4} key={report.path}>
									<ReportCard
										title={report.title}
										description={report.description}
										icon={report.icon}
										color={report.color}
										stats={report.stats}
										onClick={() =>
											navigate(`/project/${project?.idWbg}/spend-plans/${report.path}`)
										}
									/>
								</Grid>
							))}
						</Grid>

						{/* V1 reports (SOE, IFR, etc.) — still available in v2 */}
						<Box sx={{ mt: 4 }}>
							<Typography variant="h3" sx={{ mb: 2 }}>
								{t('reports')}
							</Typography>
							<Grid container spacing={2}>
								{cards.map((card, i) => {
									const { GridProps, ...cardProps } = card;
									return (
										<Grid item key={`report-${i}`} xs={12} md={6} lg={4} {...GridProps}>
											<CardAction {...cardProps} />
										</Grid>
									);
								})}
							</Grid>
						</Box>

						{/* Additional Info Section */}
						<Box
							sx={{
								mt: 4,
								p: 3,
								bgcolor: alpha(theme.palette.info.main, 0.04),
								borderRadius: 2,
								border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
							}}>
							<Stack direction="row" alignItems="center" spacing={2}>
								<Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
									<IconTrending sx={{ color: theme.palette.info.main }} />
								</Avatar>
								<Box>
									<Typography variant="subtitle1" fontWeight={600}>
										{t('spendPlan.reports.moreReportsComingSoon')}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{t('spendPlan.reports.moreReportsDescription')}
									</Typography>
								</Box>
							</Stack>
						</Box>
					</Container>
				) : (
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Grid item xs={12}>
								<Container maxWidth={false}>
									<Typography variant="h2">{t('reports')}</Typography>
								</Container>
							</Grid>
						</Grid>
						<Grid item xs={12}>
							<Container maxWidth={false}>
								<Grid container spacing={2}>
									{cards.map((card, i) => {
										const { GridProps, ...cardProps } = card;
										return (
											<Grid item key={`report-${i}`} xs={12} md={6} {...GridProps}>
												<CardAction {...cardProps} />
											</Grid>
										);
									})}
								</Grid>
							</Container>
						</Grid>
					</Grid>
				)}
			</Box>
			<DialogDownloadReport
				isOpen={paramDialog === 'downloadReport'}
				onClose={() => {
					searchParams.delete('dialog');
					searchParams.delete('dialogReport');
					setSearchParams(searchParams);
				}}
			/>
		</>
	);
};
